// Frontend ASIN helpers: normalize and validate (TypeScript)
// Mirrors backend behavior while keeping browser compatibility.

/**
 * Normalize an ASIN string defensively for downstream APIs and UI logic.
 * - Unicode NFKC normalization
 * - Remove common zero-width/format characters (ZWJ/ZWNJ/LRM/RLM/BOM/SHY/WORD JOINER)
 * - Strip all non-alphanumeric characters
 * - Uppercase the result
 */
export function normalizeAsin(raw: unknown): string {
  if (raw == null) return "";
  const s = String(raw);
  const nkfc = typeof (s as any).normalize === "function" ? s.normalize("NFKC") : s;
  // Remove common Unicode format characters that appear in pasted inputs
  // U+200C ZWNJ, U+200D ZWJ, U+200E LRM, U+200F RLM, U+FEFF BOM, U+2060 WORD JOINER, U+00AD SOFT HYPHEN
  const withoutFormat = nkfc.replace(/\u200C|\u200D|\u200E|\u200F|\uFEFF|\u2060|\u00AD/gu, "");
  // Remove everything except letters and digits
  const alnum = withoutFormat.replace(/[^0-9a-z]/gi, "");
  return alnum.toUpperCase();
}

/**
 * Returns true if the ASIN is exactly 10 alphanumeric characters (case-insensitive).
 */
export function isValidAsin(asin: unknown): boolean {
  const a = normalizeAsin(asin);
  return /^[A-Z0-9]{10}$/.test(a);
}

/**
 * Normalize and validate helper.
 */
export function normalizeAndValidateAsin(raw: unknown): { asin: string; valid: boolean } {
  const asin = normalizeAsin(raw);
  return { asin, valid: isValidAsin(asin) };
}
